import { DateTime } from 'luxon';

// see this for raw source data structure https://bitbucket.aeb.cirium.dev/projects/PDX-DP/repos/cd-processing/browse/docs/src/main/resources/OutputFormatSchemaGroup_v1.1.json#3
// filtered through this java model https://bitbucket.aeb.cirium.dev/projects/PDX-TRIPS/repos/trips-processing/browse/trips-processing-common/src/main/java/com/flightstats/trips/processing/model/pfh/ProcessedFlightHistory.java
//   for closest thing to a spec we have ( the json spec still appears to make some inconsistent claims about optionality, take it w/ a grain of salt )

// see also (but it's only loosely related, don't trust the enums) https://developer.flightstats.com/api-docs/historical-flight-status/v3/historicalFlightStatusResponse

export type Codeshare = {
  flightNumber?: string;
  trafficRestriction?: string;
  carrierDetails?: CarrierDetails;
};

export type CarrierDetails = {
  key?: string;
  fs?: string;
  iata?: string;
  name?: string;
  displayName?: string;
};

type AirportDetails = {
  city?: string;
  countryCode?: string;
  displayName?: string;
  fs?: string;
  iata?: string;
  key?: string;
  name?: string;
  searchName?: string;
  stateCode?: string;
};

type EquipmentDetails = {
  iata?: string;
  jet?: boolean;
  name?: string;
  regional?: boolean;
  turboProp?: boolean;
  widebody?: boolean;
};

type MetaData<DateType extends string | DateTime> = {
  date?: DateType;
  sourceId?: number;
  sourceExternalName?: string;
  sourceInternalName?: string;
  updated?: boolean;
};

type Change = {
  old?: string | DateTime;
  new?: string | DateTime;
};

type IrregularOperationType =
  | 'CANCELLED'
  | 'CONTINUATION_FOR'
  | 'CONTINUED_BY'
  | 'DIVERTED'
  | 'FLOWN_OVER'
  | 'FLYOVER'
  | 'INCIDENT'
  | 'OTHER'
  | 'POSITIONAL_FOR'
  | 'RECOVERED'
  | 'REINSTATED'
  | 'REMOVED_FROM_SCHEDULE'
  | 'REPLACED_BY'
  | 'REPLACEMENT_FOR'
  | 'RETURN_FROM_AIR'
  | 'RETURN_TO_GATE'
  | 'SUBSEQUENT_OPERATION_BY'
  | 'SUBSEQUENT_OPERATION_FOR';

type Creator = 'ASDI' | 'Data Source' | 'Innovata' | 'Positional';

export const FlightStatuses = {
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
  DIVERTED: 'DIVERTED',
  LANDED: 'LANDED',
  NOT_OPERATIONAL: 'NOT_OPERATIONAL',
  REDIRECTED: 'REDIRECTED',
  SCHEDULED: 'SCHEDULED',
  UNKNOWN: 'UNKNOWN',
} as const;

export type Status = keyof typeof FlightStatuses;

type GenericIrregular<DateType extends string | DateTime> = {
  type?: IrregularOperationType;
  arrival?: string;
  arrivalDetails?: AirportDetails;
  description?: string;
  linkedId?: number;
  dateTime?: DateType;
};

type GenericUpdate<DateType extends string | DateTime> = {
  metaData?: MetaData<DateType>;
  changes?: Record<string, Change>;
  irregular?: GenericIrregular<DateType>;
};

export type Update = GenericUpdate<DateTime>;

type Wetlease = {
  carrier?: string;
  scheduleText?: string;
};

type GenericProcessedFlightHistory<DateType extends string | DateTime> = {
  id: number;
  actualEquipmentIataCode?: string;
  actualEquipmentDetails?: EquipmentDetails;
  arrivalDetails?: AirportDetails;
  departureDetails?: AirportDetails;
  divertedDetails?: AirportDetails;
  carrierDetails?: CarrierDetails;
  codeshares?: Codeshare[];
  flightNumber?: string;
  scheduledEquipmentDetails?: EquipmentDetails;
  status?: Status;
  tailNumber?: string;
  creationDate?: DateType;
  creator?: Creator;
  departureTimeZone?: string;
  arrivalTimeZone?: string;
  divertedTimeZone?: string;
  // departure
  publishedDeparture?: DateType;
  departureTerminal?: string;
  departureGate?: string;
  // gate
  scheduledGateDeparture?: DateType;
  actualGateDeparture?: DateType;
  estimatedGateDeparture?: DateType;
  // runway
  scheduledRunwayDeparture?: DateType;
  actualRunwayDeparture?: DateType;
  estimatedRunwayDeparture?: DateType;
  // arrival
  publishedArrival?: DateType;
  arrivalTerminal?: string;
  arrivalGate?: string;
  // gate
  scheduledGateArrival?: DateType;
  actualGateArrival?: DateType;
  estimatedGateArrival?: DateType;
  // runway
  scheduledRunwayArrival?: DateType;
  actualRunwayArrival?: DateType;
  estimatedRunwayArrival?: DateType;
  // -------------------------------
  baggage?: string;
  updates?: GenericUpdate<DateType>[];
  linkedFlightIds?: number[];
  wetlease?: Wetlease;
};

export type Irregular = GenericIrregular<DateTime>;
export type RawProcessedFlightHistory = GenericProcessedFlightHistory<string>;
export type ProcessedFlightHistory = GenericProcessedFlightHistory<DateTime>;
